import { ChevronDownIcon, MenuIcon, NAVIGATION_ICON_SIZE } from './icons';
import { Box, Button, Paper, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import {
  accessibleCollectionName,
  collectionCapacityLabel,
  CollectionCapacitySummary,
} from './collectionContext';

import {
  AppTab,
  NavigationProps,
  InventoryActivityIcon,
  inventoryActivityLabel,
} from './navigation';

export function DesktopNavigation({
  value,
  onChange,
  embedded = false,
  leading,
  collection,
  itemActivity,
  onMore,
  moreOpen,
  moreButtonRef,
  headerAction,
}: NavigationProps & {
  embedded?: boolean;
  leading?: React.ReactNode;
  headerAction?: React.ReactNode;
  collection?: {
    name: string;
    role?: string;
    capacity?: CollectionCapacitySummary;
    onOpen: () => void;
  };
}) {
  const capacityLabel = collectionCapacityLabel(collection?.capacity);
  const collectionSecondary = [collection?.role, capacityLabel]
    .filter(Boolean)
    .join(' · ');
  return (
    <Paper
      component="nav"
      aria-label="Primary navigation"
      elevation={0}
      sx={{
        position: embedded ? 'static' : 'sticky',
        top: embedded ? 'auto' : 0,
        zIndex: (theme) => theme.zIndex.appBar,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: embedded ? 0 : 2,
        borderBottom: 0,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        borderRadius: 0,
        boxShadow: 'none',
        px: 1,
        py: 1,
        overflow: 'hidden',
      }}
    >
      {leading}
      {collection && (
        <Button
          variant="outlined"
          onClick={collection.onOpen}
          aria-label={`Choose collection: ${accessibleCollectionName(collection.name)}`}
          aria-haspopup="dialog"
          title={collectionSecondary || undefined}
          aria-description={collectionSecondary || undefined}
          sx={{
            alignSelf: 'center',
            justifyContent: 'flex-start',
            minWidth: 0,
            width: { sm: 140, md: 180 },
            px: 1.5,
            py: 0.75,
            minHeight: 44,
            border: 0,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.default',
            color: 'text.primary',
            textAlign: 'left',
            textTransform: 'none',
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              component="bdi"
              dir="auto"
              variant="body2"
              fontWeight={700}
              noWrap
            >
              {collection.name}
            </Typography>
          </Box>
          <ChevronDownIcon
            aria-hidden="true"
            fontSize="small"
            sx={{ ml: 0.5, color: 'text.secondary' }}
          />
        </Button>
      )}
      <Tabs
        data-testid="app-tabs-view-switcher"
        value={value}
        onChange={(_event, nextValue: AppTab) => onChange(nextValue)}
        variant="standard"
        aria-label="Inventory views"
        sx={{
          flex: '0 1 auto',
          minWidth: 0,
          minHeight: 48,
          '& .MuiTabs-flexContainer': { gap: 1, p: 0.75 },
          '& .MuiTab-root': {
            minHeight: 48,
            py: 1,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.secondary',
            '&.Mui-selected': { color: 'primary.main' },
          },
        }}
      >
        <Tab
          value="items"
          label="Inventory"
          aria-label={inventoryActivityLabel(itemActivity)}
          icon={
            itemActivity?.count ? (
              <InventoryActivityIcon activity={itemActivity} />
            ) : undefined
          }
          iconPosition="start"
        />
        <Tab value="locations" label="Locations" />
      </Tabs>
      {headerAction && (
        <Box sx={{ ml: 'auto', flexShrink: 0 }}>{headerAction}</Box>
      )}
      {onMore && (
        <Button
          variant="outlined"
          type="button"
          ref={moreButtonRef}
          aria-label="Menu"
          aria-haspopup="dialog"
          aria-expanded={moreOpen ? 'true' : undefined}
          onClick={onMore}
          sx={{
            minWidth: 100,
            ml: headerAction ? 0 : 'auto',
            minHeight: 48,
            py: 1,
            flexDirection: 'row',
            gap: 1,
            color: 'text.secondary',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            borderRadius: 1,
          }}
        >
          <MenuIcon sx={{ fontSize: NAVIGATION_ICON_SIZE }} />
          <span>Menu</span>
        </Button>
      )}
    </Paper>
  );
}
